import { useMemo, useState } from "react";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { FormatDateTime } from "../../../../../utils/functions/ResourceFunctions";
import { useSearchFilter } from "../../../../../utils/functions/useDate";
import {
  useDeleteRatingScaleMutation,
  useGetRatingsQuery,
} from "../../../../api/hrApi";
import DashboardStyle from "../../../../dashboard/Styles/Dashboard.module.css";
import { CTAButtons } from "../../../../global/components/Buttons/buttons";
import PageStyle from "../../../Components/Layout/PageLayout";
import { TableActions } from "../../../Components/Misc/Actions";
import DeleteItemModal from "../../../Components/Modals/DeleteItemModal";
import { AppModalTemplate } from "../../../Components/Modals/Modals";
import Pagination from "../../../Components/Pagination/Pagination";
import {
  CalendarFilter2,
  ProDropFilter,
  SearchFilter,
} from "../../../Components/Search/Search";
import Table from "../../../Components/Table/Table";
import { getStatusText } from "../../Finance/CostCenter/Class";
import EditRatingScaleModal from "./Modals/EditRatingModal";
import RatingScaleModal from "./Modals/RatingScaleModal";
import { FilterByStatus } from "./status";
const RatingScale = () => {
  const [onDelete] = useDeleteRatingScaleMutation();
  const [openModal, setOpenModal] = useState(false);
  const [openCreatModal, setOpenCreatModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const navigate = useNavigate();
  const handleModal = () => {
    setOpenModal(!openModal);
  };
  const handleOpenCreateModale = () => {
    setOpenCreatModal(!openCreatModal);
  };
  const {
    searchText,
    setSearchText,
    pageSize,
    setPageSize,
    setCurrentPage,
    currentPage,
    filter,
    setFilter,
  } = useSearchFilter({
    initialFilter: 1,
  });
  const { data, isLoading } = useGetRatingsQuery({
    PageSize: pageSize,
    PageNumber: currentPage,
    GeneralSearch: searchText,
    StartDate: startDate,
    EndDate: endDate,
    ApprovalStatus: filter,
  });
  const actionButton = (
    <>
      <CTAButtons onClick={handleOpenCreateModale}>
        Create Rating Scale
      </CTAButtons>
    </>
  );
  const handleOpenDeleteModal = (id) => {
    if (id) {
      setSelectedId(id);
      setOpenDelete(true);
    }
  };

  const handleCloseDelete = () => {
    setSelectedId(null);
    setOpenDelete(false);
  };
  const handleDelete = async () => {
    try {
      const response = await onDelete(selectedId).unwrap();
      if (response?.successful) {
        setOpenDelete(false);
        toast.success(response?.statusMessage);
      } else {
        toast.error(response?.statusMessage);
      }
    } catch (error) {
      toast.error(error.data?.statusMessage ?? "Error deleting rating scale");
    }
  };
  const handleView = (id) => {
    if (id) {
      navigate(`${id}/view`);
    }
  };
  const handleEdit = (id) => {
    if (id) {
      setSelectedId(id);
      handleModal();
    }
  };
  const dataAvailable = Boolean(data?.data?.length > 0);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!data?.data) return [];
    return [...data.data].sort((a, b) => {
      if (!sortColumn) return 0;
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      if (sortColumn === "scaleLevels") {
        aValue = aValue.length;
        bValue = bValue.length;
      }

      if (sortColumn === "createdDate") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data?.data, sortColumn, sortDirection]);

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort className="ml-1" />;
    return sortDirection === "asc" ? (
      <FaSortUp className="ml-1" />
    ) : (
      <FaSortDown className="ml-1" />
    );
  };

  return (
    <PageStyle
      action={actionButton}
      hasBack={true}
      hasTopPageDemo
      backURL={"/dashboard/hr/HR-Appraisal-Setup"}
      support={
        " Creates Period for the year which will then be used when creating an objective."
      }
      demoTitle={"Manage Rating Scale"}
    >
      <form className={DashboardStyle.dashboard_filter}>
        <SearchFilter
          value={searchText}
          text={(searchText) => setSearchText(searchText)}
        />
        <CalendarFilter2
          name="Select Date Range"
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          normalDate
        />
        <ProDropFilter
          filter={filter}
          setFilter={setFilter}
          name={"Status"}
          filterBy={FilterByStatus}
        />
      </form>
      <div className={DashboardStyle.dashboard_table_holder}>
        <Table>
          <thead>
            <tr>
              <th>ID</th>
              <th
                onClick={() => handleSort("title")}
                style={{ cursor: "pointer" }}
              >
                Title {renderSortIcon("title")}
              </th>
              <th
                onClick={() => handleSort("scaleLevels")}
                style={{ cursor: "pointer" }}
              >
                Scale Levels {renderSortIcon("scaleLevels")}
              </th>
              <th
                onClick={() => handleSort("createdBy")}
                style={{ cursor: "pointer" }}
              >
                Initiator's Name {renderSortIcon("createdBy")}
              </th>
              <th
                onClick={() => handleSort("approvalStatus")}
                style={{ cursor: "pointer" }}
              >
                Approval Status {renderSortIcon("approvalStatus")}
              </th>
              <th
                onClick={() => handleSort("createdDate")}
                style={{ cursor: "pointer" }}
              >
                Created Date {renderSortIcon("createdDate")}
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="text-center">
                  Loading...
                </td>
              </tr>
            )}
            {dataAvailable &&
              sortedData?.map((item, index) => {
                return (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.title}</td>
                    <td>{item.scaleLevels.length}</td>
                    <td>{item?.createdBy}</td>
                    <td>
                      {typeof item?.approvalStatus === "number"
                        ? getStatusText(item?.approvalStatus)
                        : item?.approvalStatus}
                    </td>
                    <td>{FormatDateTime(item.createdDate, "DD/MM/YYYY")}</td>
                    <td>
                      <TableActions hasChildren={true} url={`${item.id}/view`}>
                        {[
                          {
                            name: item?.approvalStatus !== 0 ? "Edit" : null,
                            action:
                              item?.approvalStatus !== 0
                                ? () => handleEdit(item.id)
                                : null,
                          },
                          {
                            name: "View",
                            action: () => handleView(item.id),
                          },
                          {
                            name: item?.approvalStatus !== 0 ? "Delete" : null,
                            action: () =>
                              item?.approvalStatus !== 0
                                ? handleOpenDeleteModal(item.id)
                                : null,
                          },
                        ]}
                      </TableActions>
                    </td>
                  </tr>
                );
              })}
            {sortedData?.length === 0 && !isLoading && (
              <tr>
                <td colSpan={6} className="text-center">
                  No Rating Scales Found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <Pagination
        last_page={data?.metaData?.totalPages}
        present_page={data?.metaData.page}
        totalRows={data?.total}
        pageSize={pageSize}
        setPageSize={(page) => setPageSize(page)}
        click={(page) => setCurrentPage(page)}
      />
      {openModal && selectedId && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openModal}
          setIsOpen={handleModal}
        >
          <EditRatingScaleModal
            isOpen={openModal}
            setIsOpen={setOpenModal}
            onValidate={handleModal}
            id={selectedId}
          />
        </AppModalTemplate>
      )}
      {openCreatModal && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openCreatModal}
          setIsOpen={handleOpenCreateModale}
        >
          <RatingScaleModal
            isOpen={openCreatModal}
            setIsOpen={setOpenCreatModal}
            onValidate={handleOpenCreateModale}
          />
        </AppModalTemplate>
      )}

      <DeleteItemModal
        isOpen={openDelete}
        handleClose={handleCloseDelete}
        handleDelete={handleDelete}
        headText={"Delete Rating Scale"}
        infoText={"Are you sure you want to Delete this Rating Scale?"}
      />
    </PageStyle>
  );
};

export default RatingScale;
